/**
 * Multi-select product picker used to build the printable catalog.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmt } from "@/lib/api";
import { Image as ImageIcon, X, MagnifyingGlass, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

export function CatalogPicker({ products, onClose }) {
  const navigate = useNavigate();
  const MAX = 9;
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX) {
        toast.info(`Máximo ${MAX} productos por hoja`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const filtered = products.filter((p) => {
    const term = q.trim().toLowerCase();
    if (!term) return true;
    return (
      (p.nombre || "").toLowerCase().includes(term) ||
      (p.categoria || "").toLowerCase().includes(term)
    );
  });

  const goPrint = () => {
    if (selected.length === 0) return;
    navigate(`/catalogo-imprimible?ids=${selected.join(",")}`);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="catalog-picker"
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="overline">Hoja imprimible</div>
            <div className="h-display text-xl font-bold">Selecciona productos</div>
            <div className="text-xs text-muted2 mt-0.5">
              Hasta {MAX} productos por hoja A4 · {selected.length}/{MAX} seleccionados
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100"
            data-testid="catalog-picker-close"
          >
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-zinc-100">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
            <input
              type="text"
              className="input-flat pl-8"
              placeholder="Filtra por nombre o categoría…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="catalog-picker-search"
            />
          </div>
        </div>

        <ul className="flex-1 overflow-y-auto divide-y divide-zinc-100">
          {filtered.length === 0 ? (
            <li className="px-5 py-10 text-center text-sm text-muted2">
              No hay productos.
            </li>
          ) : (
            filtered.map((p) => {
              const imgs = p.imagenes_base64?.length
                ? p.imagenes_base64
                : p.imagen_base64
                  ? [p.imagen_base64]
                  : [];
              const thumb = imgs[0] ? `data:image/jpeg;base64,${imgs[0]}` : null;
              const isSelected = selected.includes(p.id);
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-zinc-50 ${
                      isSelected ? "bg-klein/5" : ""
                    }`}
                    data-testid={`catalog-pick-${p.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="shrink-0"
                    />
                    <div className="w-10 h-10 border border-zinc-200 bg-zinc-50 overflow-hidden shrink-0">
                      {thumb ? (
                        <img src={thumb} alt={p.nombre} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon size={14} className="text-zinc-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.nombre}</div>
                      <div className="text-xs text-muted2">
                        {p.categoria} · {fmt(p.precio_venta)}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <footer className="px-5 py-4 border-t border-zinc-200 flex items-center justify-between gap-2 bg-zinc-50">
          <button
            onClick={onClose}
            className="btn-ghost"
            data-testid="catalog-picker-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={goPrint}
            disabled={selected.length === 0}
            className="btn-klein disabled:opacity-50"
            data-testid="catalog-picker-print"
          >
            <Printer size={16} weight="bold" /> Generar hoja ({selected.length})
          </button>
        </footer>
      </div>
    </div>
  );
}
